/* ========== FILLED MAP CHART ==========
 * Excel's Filled Map renders real geographic polygons via Bing Maps. The
 * geo data is an opaque <cx:geoCache> binary blob we can't decode.
 *
 * When the chart's <cx:geography cultureRegion="XX"> tells us which
 * country, we render a recognizable outline of that country (currently
 * India only — extend MAP_OUTLINES below to add more). Otherwise we fall
 * back to a stylized organic blob.
 *
 * Layout mirrors Excel:
 *   ┌────────────────────────────────────┐
 *   │             Filled Map             │  ← title (from renderSimpleChart)
 *   │                                    │
 *   │   ╱⌒⌒⌒╲             [series name] │
 *   │  ⟨ region ⟩            ▓▓▓▓▓▓▓     │  ← stylized region polygon
 *   │   ╲    ╱            value          │     on the left; legend on
 *   │    ⌒⌒⌒                ░ → ▓        │     the right (matches Excel)
 *   └────────────────────────────────────┘
 *
 * The region shape is procedurally generated from the chart's id so it
 * looks like an actual geographic blob (not a perfect circle). Color is
 * a choropleth lightness based on value / max.
 * ======================================== */

// Country outline coordinates — array of [x, y] points in a normalized 0–1
// coordinate space (x: 0=west, 1=east, y: 0=north, 1=south). Each polygon
// roughly captures the country's recognizable silhouette. Add more
// entries here keyed by ISO 3166 alpha-2 code (matching cultureRegion).
const MAP_OUTLINES = {
  IN: [
    // India: rough silhouette anchored by Kashmir tip (top), Gujarat (west),
    // Kanyakumari (south tip), Bengal (east).
    [0.42, 0.05], [0.50, 0.04], [0.60, 0.06], [0.66, 0.10], [0.70, 0.14],
    [0.74, 0.18], [0.74, 0.22], [0.78, 0.24], [0.82, 0.22], [0.88, 0.24],
    [0.90, 0.30], [0.86, 0.34], [0.84, 0.38], [0.80, 0.42], [0.78, 0.48],
    [0.74, 0.54], [0.70, 0.60], [0.66, 0.66], [0.62, 0.72], [0.58, 0.80],
    [0.54, 0.88], [0.50, 0.95], [0.46, 0.92], [0.44, 0.84], [0.42, 0.76],
    [0.38, 0.70], [0.34, 0.66], [0.28, 0.62], [0.22, 0.58], [0.18, 0.52],
    [0.16, 0.46], [0.18, 0.42], [0.20, 0.38], [0.22, 0.34], [0.20, 0.30],
    [0.18, 0.26], [0.20, 0.22], [0.24, 0.20], [0.28, 0.18], [0.32, 0.16],
    [0.34, 0.12], [0.38, 0.08],
  ],
};

export function drawFilledMapChart(ctx, x, y, width, height, chartObj) {
  try {
    console.log('[drawFilledMapChart]', chartObj && chartObj.id, 'region:', chartObj && chartObj.geography && chartObj.geography.cultureRegion);
  } catch (e) { /* swallow */ }

  // ── Pull data ─────────────────────────────────────────────────────────
  var series = chartObj && chartObj.series && chartObj.series[0] ? chartObj.series[0] : null;
  var seriesName = (series && series.name) || (chartObj && chartObj.title) || '';
  var rawCats = (chartObj && chartObj.categories) ? chartObj.categories : [];
  var rawVals = (series && series.data) ? series.data : [];

  // Filter spurious numeric labels (header rows bleeding in)
  var cats = rawCats.filter(function (c) {
    if (c == null) return false;
    var s = String(c).trim();
    if (!s) return false;
    return isNaN(parseFloat(s));
  });

  var rowCount = Math.max(cats.length, rawVals.length, 1);
  var regions = [];
  for (var i = 0; i < rowCount; i++) {
    regions.push({
      label: cats[i] || ('Region ' + (i + 1)),
      value: rawVals[i] != null ? rawVals[i] : (rawVals[rawVals.length - 1] || 0),
    });
  }

  var maxVal = Math.max.apply(null, regions.map(function (r) { return Math.abs(r.value); }).concat([1]));
  var minVal = Math.min.apply(null, regions.map(function (r) { return Math.abs(r.value); }).concat([0]));

  // ── Layout: map on left ~62% of width, legend on right ───────────────
  var legendW = Math.min(140, width * 0.32);
  var mapAreaX = x + 8;
  var mapAreaY = y + 8;
  var mapAreaW = width - legendW - 24;
  var mapAreaH = height - 16;
  var legendX = x + width - legendW - 8;
  var legendY = y + 12;

  // ── Draw the region polygon(s) ────────────────────────────────────────
  // Prefer a real country outline when cultureRegion matches one we have.
  // For single-region maps this gives a recognizable country silhouette.
  var cultureRegion = (chartObj && chartObj.geography && chartObj.geography.cultureRegion) || '';
  var countryOutline = MAP_OUTLINES[cultureRegion.toUpperCase()];

  if (regions.length === 1 && countryOutline) {
    drawCountryOutline(
      ctx,
      mapAreaX, mapAreaY, mapAreaW, mapAreaH,
      countryOutline,
      regions[0],
      maxVal,
    );
  } else if (regions.length === 1) {
    drawRegionBlob(
      ctx,
      mapAreaX + mapAreaW / 2,
      mapAreaY + mapAreaH / 2,
      Math.min(mapAreaW, mapAreaH) * 0.42,
      regions[0],
      maxVal,
      seedFromString((chartObj && chartObj.id) || '0'),
    );
  } else {
    var cols = Math.ceil(Math.sqrt(regions.length));
    var rows = Math.ceil(regions.length / cols);
    var cellW = mapAreaW / cols;
    var cellH = mapAreaH / rows;
    regions.forEach(function (region, idx) {
      var col = idx % cols;
      var row = Math.floor(idx / cols);
      drawRegionBlob(
        ctx,
        mapAreaX + col * cellW + cellW / 2,
        mapAreaY + row * cellH + cellH / 2,
        Math.min(cellW, cellH) * 0.42,
        region,
        maxVal,
        seedFromString((chartObj && chartObj.id) + ':' + idx),
      );
    });
  }

  // ── Legend on the right ──────────────────────────────────────────────
  // Series name label at top
  if (seriesName) {
    ctx.fillStyle = '#444';
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    var nameLines = wrapText(ctx, seriesName, legendW - 8);
    nameLines.forEach(function (line, li) {
      ctx.fillText(line, legendX, legendY + li * 14);
    });
    legendY += nameLines.length * 14 + 8;
  }

  // Color gradient bar (vertical) with min/max labels
  var gradH = Math.min(140, height - (legendY - y) - 28);
  var gradW = 14;
  var gradX = legendX + legendW - gradW - 4;
  var gradY = legendY;
  for (var gi = 0; gi < gradH; gi++) {
    var t = gi / gradH;             // 0 at top = max value; 1 at bottom = min
    var ratio = 1 - t;
    var lightness = 92 - Math.round(ratio * 60);
    ctx.fillStyle = 'hsl(210, 65%, ' + lightness + '%)';
    ctx.fillRect(gradX, gradY + gi, gradW, 1);
  }
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1;
  ctx.strokeRect(gradX, gradY, gradW, gradH);

  // Value label(s) next to gradient — show the single value when there's
  // just one region (matches Excel "24000" label), or min/max otherwise.
  ctx.fillStyle = '#333';
  ctx.font = '10px Arial';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  if (regions.length === 1) {
    ctx.fillText(Number(regions[0].value).toLocaleString(), gradX - 6, gradY + gradH / 2);
  } else {
    ctx.fillText(Number(maxVal).toLocaleString(), gradX - 6, gradY + 6);
    ctx.fillText(Number(minVal).toLocaleString(), gradX - 6, gradY + gradH - 6);
  }

  // Attribution corner — keeps parity with Excel's "Powered by Bing"
  ctx.fillStyle = '#999';
  ctx.font = '8px Arial';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillText('Powered by viewer', x + width - 8, y + height - 4);
}

/**
 * Draw a recognizable country outline from normalized polygon coordinates.
 * The outline is scaled to fit `boxW × boxH` preserving aspect ratio.
 */
function drawCountryOutline(ctx, boxX, boxY, boxW, boxH, normalizedPts, region, maxVal) {
  if (!normalizedPts || normalizedPts.length === 0) return;

  var ratio = Math.abs(region.value) / (maxVal || 1);
  var lightness = 78 - Math.round(ratio * 50);
  var fill = 'hsl(210, 65%, ' + lightness + '%)';

  // Compute aspect ratio from the normalized points (most outlines aren't
  // square — India is taller than wide). Maintain it when scaling to box.
  var minX = 1, maxX = 0, minY = 1, maxY = 0;
  normalizedPts.forEach(function (p) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  });
  var srcW = maxX - minX || 1;
  var srcH = maxY - minY || 1;

  // Fit inside box, preserving aspect ratio
  var scale = Math.min(boxW / srcW, boxH / srcH) * 0.92;
  var drawnW = srcW * scale;
  var drawnH = srcH * scale;
  var offX = boxX + (boxW - drawnW) / 2 - minX * scale;
  var offY = boxY + (boxH - drawnH) / 2 - minY * scale;

  ctx.beginPath();
  normalizedPts.forEach(function (p, i) {
    var px = offX + p[0] * scale;
    var py = offY + p[1] * scale;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  });
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Optional region label centered inside the shape
  if (drawnW > 60 && drawnH > 80 && region.label) {
    ctx.fillStyle = lightness > 60 ? '#1F4E79' : '#FFFFFF';
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    var label = region.label.length > 20 ? region.label.substring(0, 19) + '…' : region.label;
    ctx.fillText(label, boxX + boxW / 2, boxY + boxH / 2);
  }
}

/**
 * Draw a single "region" — an organic irregular polygon centered at (cx,cy)
 * with approximate radius r. The shape uses a procedural noise based on
 * `seed` so different regions look different but each chart is stable.
 */
function drawRegionBlob(ctx, cx, cy, r, region, maxVal, seed) {
  var ratio = Math.abs(region.value) / (maxVal || 1);
  var lightness = 80 - Math.round(ratio * 50);
  var fill = 'hsl(210, 65%, ' + lightness + '%)';

  // Generate ~16 polygon vertices around (cx,cy) with seeded jitter on r
  var verts = 16;
  ctx.beginPath();
  for (var i = 0; i < verts; i++) {
    var ang = (i / verts) * Math.PI * 2;
    // Pseudo-random radius wobble: 0.78–1.12 of nominal r
    var wobble = 0.78 + 0.34 * pseudoRandom(seed + i * 0.731);
    var px = cx + Math.cos(ang) * r * wobble;
    var py = cy + Math.sin(ang) * r * wobble;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = '#FFFFFF';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Region label centered when there's room
  if (r > 30) {
    ctx.fillStyle = lightness > 60 ? '#1F4E79' : '#FFFFFF';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    var label = region.label.length > 18 ? region.label.substring(0, 17) + '…' : region.label;
    ctx.fillText(label, cx, cy);
  }
}

/** Deterministic pseudo-random in [0,1) from a numeric seed. */
function pseudoRandom(seed) {
  var x = Math.sin(seed * 9999.0) * 43758.5453;
  return x - Math.floor(x);
}

/** Hash a string into a numeric seed. */
function seedFromString(s) {
  var h = 0;
  for (var i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 100000 / 100;
}

/** Wrap text into lines that fit within maxWidth. */
function wrapText(ctx, text, maxWidth) {
  var words = String(text).split(' ');
  var lines = [];
  var current = '';
  for (var i = 0; i < words.length; i++) {
    var test = current ? current + ' ' + words[i] : words[i];
    if (ctx.measureText(test).width <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = words[i];
    }
  }
  if (current) lines.push(current);
  return lines;
}
